// WS 协议类型 — 严格对应 shared/pigwords/11-sdd-ws-protocol.md

export type Phase =
  // v1 phases (后端 D2 后实际推送)
  | 'lobby'
  | 'role_reveal'
  | 'word_pick'
  | 'playing'
  | 'end'
  // v0 phases 保留 union 兼容旧 store/test，运行时已不会出现
  | 'ready_window'
  | 'night'
  | 'day'
  | 'werewolf_counterattack'
  | 'settlement';

export type BaseRole = 'wolf' | 'seer' | 'guard' | 'villager';

export type GameEndResult = 'good_win' | 'wolf_win' | 'aborted';

export type Role = 'chief' | 'wolf' | 'seer' | 'guard' | 'villager';

export type Indicator = 'yes' | 'no' | 'maybe' | 'close' | 'irrelevant' | 'correct';

export const INDICATOR_LIST: Indicator[] = [
  'yes',
  'no',
  'maybe',
  'close',
  'irrelevant',
  'correct',
];

export const INDICATOR_LABEL: Record<Indicator, string> = {
  yes: '是',
  no: '否',
  maybe: '也许',
  close: '接近',
  irrelevant: '不相关',
  correct: '猜对了',
};

export const ROLE_LABEL: Record<Role, string> = {
  chief: '村长',
  wolf: '狼',
  seer: '见者',
  guard: '守卫',
  villager: '村民',
};

export type ClientMessageType =
  | 'HELLO'
  | 'SET_NICKNAME'
  // v1 新事件（SDD-WS v1 §4）
  | 'START_GAME'
  | 'CONFIRM_WORD'
  | 'REROLL_CANDIDATES'
  | 'SWIPE'
  | 'END_GAME'
  | 'CHIEF_REVISE'
  | 'ROLE_REVEAL_ACK'
  | 'RETURN_LOBBY'
  // v0 废弃事件（SDD-WS v1 §4.99）：D3/D4 重写后移除
  | 'READY'
  | 'UNREADY'
  | 'START_REQUEST'
  | 'START_VOTE'
  | 'CANCEL_START'
  | 'CHIEF_LONG_PRESS_BEGIN'
  | 'CHIEF_LONG_PRESS_CANCEL'
  | 'CHIEF_LONG_PRESS_COMMIT'
  | 'CHIEF_DECLARE_CORRECT'
  | 'WOLF_ACCUSE'
  | 'PING';

export interface ClientMessage<P = unknown> {
  type: ClientMessageType;
  reqId: string;
  ts: number;
  payload: P;
}

export type ServerMessageType =
  | 'STATE_SNAPSHOT'
  | 'RESUME_OK'
  | 'PLAYER_JOIN'
  | 'PLAYER_LEAVE'
  | 'PLAYER_UPDATE'
  | 'PHASE_CHANGE'
  | 'PRIVATE_ROLE'
  // v1 新增（SDD-WS v1 §3）
  | 'START_GAME_BROADCAST'
  | 'START_GAME_TRIGGERED'
  | 'WORD_PICK_STARTED'
  | 'CANDIDATE_WORDS'
  | 'WORD_PICKED'
  | 'SWIPE_RECORDED'
  | 'GAME_END'
  | 'CHIEF_REVISED'
  // v0 废弃（服务端 v1 不再推送；D3/D4 重写后移除处理分支）
  | 'READY_UPDATE'
  | 'START_REQUEST_BROADCAST'
  | 'START_REQUEST_CANCELLED'
  | 'INDICATOR_BROADCAST'
  | 'CHIEF_CORRECT_DECLARED'
  | 'WOLF_ACCUSE_PRIVATE_ACK'
  | 'MATCH_RESULT'
  | 'SESSION_RESET'
  | 'ACK'
  | 'ERROR'
  | 'PONG';

export interface ServerMessage<P = unknown> {
  type: ServerMessageType;
  eventId: number;
  ts: number;
  payload: P;
}

// ───────── payload 形状 ─────────

export interface PlayerPublic {
  token: string;
  nickname: string;
  ready: boolean;
  online: boolean;
  isChief: boolean;
  inMatch: boolean;
}

export interface WaitlistEntry {
  token: string;
  nickname: string;
  joinedAt: number;
}

export interface IndicatorEvent {
  eventId: number;
  ts: number;
  targetToken: string;
  indicator: Indicator;
}

export interface StartRequestPayload {
  initiatorToken: string;
  agreeTokens: string[];
  deadlineTs: number;
  needAll?: number;
}

export interface PrivateRolePayload {
  // role 在 v1 里仍是 chief/wolf/seer/guard/villager；isChief 单独标识村长身份（村长本人 baseRole 是其它角色）
  role: Role;
  baseRole?: BaseRole;
  isChief?: boolean;
  word: string | null;
  teammates: string[] | null;
  seerToken: string | null;
}

// v2.1: 村长收到候选词（schema 漂移修复：candidates 取代 words）
export interface CandidateWordItem {
  id: number;
  text: string;
}
export interface CandidateWordsPayload {
  candidates: CandidateWordItem[];
  /** 软截止时间（暂存不渲染） */
  softDeadlineTs?: number;
  chiefToken?: string;
  /** 兼容字段：旧后端若仍发 candidateCount 可读 */
  candidateCount?: number;
}

// v1: 任意玩家可发起的 START_GAME 后服务端立即广播
export interface StartGameTriggeredPayload {
  initiatorToken: string;
  startedAt: number;
}

// v1: 进入 word_pick 阶段时通知（村长身份 + 倒计时）
export interface WordPickStartedPayload {
  chiefToken: string;
  candidateCount: number;
  deadlineTs?: number;
}

// v1: 选词完成，仅知情者（先知/狼）payload 含 word
export interface WordPickedPayload {
  chiefToken: string;
  word?: string; // 仅村长/先知/狼
}

export interface SwipeRecordedPayload {
  swipeCount: number;
  lastIndicator?: Indicator;
  targetToken?: string;
}

export interface GameEndPayload {
  result: GameEndResult;
  word?: string;
  revealedRoles: Array<{ token: string; nickname: string; baseRole: BaseRole; isChief: boolean }>;
  returnLobbyDeadlineTs?: number;
}

export interface PhaseChangePayload {
  phase: Phase;
  startedAt: number;
  deadlineTs: number | null;
  matchId?: number;
}

export interface IndicatorBroadcastPayload {
  chiefToken?: string; // 按 SDD §4.10：不暴露按下者，但服务端仍可发；前端容忍存在
  targetToken: string;
  indicator: Indicator;
  ts: number;
}

// v1：backend `transitions.ts` endMatch() 发的 MATCH_RESULT payload。
// 字段以后端为 source of truth；前端 reducer 由此派生 endResult/revealedRoles。
export interface MatchResultPayload {
  matchId: number;
  result: GameEndResult;
  word: string;
  chiefToken: string;
  endTriggeredBy:
    | 'chief_end_game'
    | 'wolf_accuse'
    | 'playing_timeout'
    | 'role_reveal_timeout'
    | 'word_pick_timeout'
    | string;
  roles: Array<{ token: string; nickname: string; role: Role | null }>;
  // D4 填充；D1 期间发空壳子
  swipeStats: {
    total: number;
    buckets: Array<{ indicator: Indicator; count: number }>;
    perSecond: number;
    durationMs: number;
  };
  returnLobbyDeadlineTs: number;
}

export interface SessionSnapshot {
  self: {
    token: string;
    nickname: string;
    ready: boolean;
    role: Role | null;
    privateWord: string | null;
    teammates: string[] | null;
    seerToken: string | null;
    // backend snapshot.ts 写入；村长断线重连必须能恢复
    baseRole?: BaseRole | null;
    isChief?: boolean;
    inMatch?: boolean;
  };
  phase: Phase;
  phaseStartedAt: number;
  phaseDeadlineAt: number | null;
  players: PlayerPublic[];
  waitlist: WaitlistEntry[];
  match: {
    matchId: number;
    startedAt: number;
    indicators: IndicatorEvent[];
    chiefCorrectDeclared: boolean;
  } | null;
  startRequest: StartRequestPayload | null;
  lastEventId: number;
}

export interface ErrorPayload {
  code: string;
  message?: string;
  inReplyTo?: string;
}
